// Botao "Buscar" colado a um campo CNPJ. Quando clicado consulta DUAS fontes e
// devolve os dados combinados via callback. Reusavel em qualquer formulario PJ
// (fornecedores, clientes) — quem usa decide quais campos preencher no `onResult`.
//
//  1. BrasilAPI (gratuita, sem auth, CORS aberto) — cadastro FEDERAL da Receita:
//     razao social, nome fantasia, email, telefone, endereco, situacao. NAO tem IE.
//  2. SEFAZ-GO via nosso backend (GET /api/clientes/consulta-cadastro/:cnpj) —
//     cadastro ESTADUAL: Inscricao Estadual + situacao + codigo IBGE do municipio.
//     So responde por contribuintes de GO; outro estado volta sem IE (segue manual).
//
// A IE e o codigo IBGE saem da SEFAZ (mais autoritativa). Email/telefone saem da
// BrasilAPI (a SEFAZ nao tem). A consulta a SEFAZ e best-effort: se falhar, o
// resto continua preenchendo normal e so a IE fica manual.
import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useToast } from '../Toast';
import { api } from '../../lib/api';

export interface CnpjDados {
  cnpj: string;            // so digitos (14)
  razao_social: string;
  nome_fantasia: string;
  inscricao_estadual: string; // da SEFAZ-GO ('' quando nao-contribuinte / outro estado)
  codigo_ibge_cidade: string; // da SEFAZ-GO (cMun) — '' quando indisponivel
  email: string;
  telefone: string;        // primeiro DDD+numero
  whatsapp: string;        // mesmo que telefone — operador ajusta se for outro
  cep: string;
  endereco: string;        // logradouro completo (tipo + nome, ex: "RUA F")
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;          // municipio
  uf: string;
  situacao_cadastral: string;  // 'ATIVA' | 'BAIXADA' | etc — usado pra warning
}

// Resposta do GET /api/clientes/consulta-cadastro/:cnpj (SEFAZ-GO CadConsultaCadastro4).
interface CadastroSefaz {
  encontrado: boolean;
  inscricao_estadual: string | null;
  habilitado: boolean | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  codigo_ibge_cidade: string | null;
}

interface Props {
  cnpj: string;
  onResult: (d: CnpjDados) => void;
  disabled?: boolean;
}

// A Receita guarda o logradouro em DUAS pecas: o tipo ("RUA", "AVENIDA") e o nome
// ("F", "GOIAS"). Pegar so o nome perde o "RUA" — e quando o nome e curto sobra 1
// letra, que quebra o schema da NF-e (xLgr exige 2+ chars) e volta como cStat 225
// generico. Mordeu na TROL (2026-07-22): "RUA F" chegou ao banco como "F".
export function montarLogradouro(tipo?: string | null, nome?: string | null): string {
  const t = String(tipo ?? '').trim();
  const n = String(nome ?? '').trim();
  if (!t) return n;
  if (!n) return t;
  // alguns registros ja trazem o tipo dentro do nome — nao duplicar
  const jaTemTipo = new RegExp(`^${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(n);
  return jaTemTipo ? n : `${t} ${n}`;
}

function montarTelefone(ddd?: string | null, num?: string | null): string {
  if (!num) return '';
  const numDigits = String(num).replace(/\D/g, '');
  if (!numDigits) return '';
  const dddDigits = String(ddd ?? '').replace(/\D/g, '');
  return dddDigits ? `${dddDigits}${numDigits}` : numDigits;
}

export function BuscarCnpjButton({ cnpj, onResult, disabled }: Props) {
  const toast = useToast();
  const [carregando, setCarregando] = useState(false);

  const digits = (cnpj ?? '').replace(/\D/g, '');
  const podeBuscar = digits.length === 14 && !disabled && !carregando;

  async function buscar() {
    if (!podeBuscar) {
      if (digits.length !== 14) toast.erro('CNPJ deve ter 14 digitos');
      return;
    }
    setCarregando(true);
    try {
      // Dispara as duas fontes em paralelo (a SEFAZ pode demorar — nao serializar).
      // BrasilAPI e o caminho critico; a SEFAZ e best-effort (catch -> null).
      const [r, sefaz] = await Promise.all([
        fetch(`https://brasilapi.com.br/api/cnpj/v1/${digits}`),
        api.get<CadastroSefaz>(`/api/clientes/consulta-cadastro/${digits}`).catch(() => null),
      ]);
      if (r.status === 404) {
        toast.erro('CNPJ nao encontrado na Receita Federal');
        return;
      }
      if (!r.ok) {
        toast.erro(`Erro ${r.status} ao consultar CNPJ`);
        return;
      }
      const d = await r.json();
      const ie = (sefaz?.encontrado && sefaz.inscricao_estadual) ? sefaz.inscricao_estadual.trim() : '';
      const dados: CnpjDados = {
        cnpj: digits,
        // SEFAZ (estadual, autoritativa) tem prioridade na razao/fantasia; BrasilAPI completa.
        razao_social: (sefaz?.razao_social || d.razao_social || '').trim(),
        nome_fantasia: (sefaz?.nome_fantasia || d.nome_fantasia || '').trim(),
        inscricao_estadual: ie,
        codigo_ibge_cidade: (sefaz?.encontrado && sefaz.codigo_ibge_cidade) ? sefaz.codigo_ibge_cidade.trim() : '',
        email: (d.email ?? '').trim(),
        telefone: montarTelefone(d.ddd_telefone_1?.slice(0, 2), d.ddd_telefone_1?.slice(2)),
        whatsapp: montarTelefone(d.ddd_telefone_1?.slice(0, 2), d.ddd_telefone_1?.slice(2)),
        cep: String(d.cep ?? '').replace(/\D/g, ''),
        endereco: montarLogradouro(d.descricao_tipo_de_logradouro, d.logradouro),
        numero: String(d.numero ?? '').trim(),
        complemento: (d.complemento ?? '').trim(),
        bairro: (d.bairro ?? '').trim(),
        cidade: (d.municipio ?? '').trim(),
        uf: (d.uf ?? '').trim().toUpperCase(),
        situacao_cadastral: String(d.descricao_situacao_cadastral ?? '').toUpperCase(),
      };
      onResult(dados);
      if (dados.situacao_cadastral && dados.situacao_cadastral !== 'ATIVA') {
        toast.erro(`Atencao: CNPJ ${dados.situacao_cadastral} na Receita`);
      } else if (ie) {
        toast.sucesso(`Dados importados (Receita + IE ${ie} da SEFAZ-GO)`);
      } else {
        toast.sucesso('Dados importados da Receita Federal (IE nao localizada na SEFAZ-GO)');
      }
    } catch (e: any) {
      toast.erro('Falha na conexao com a BrasilAPI: ' + (e?.message ?? 'erro de rede'));
    } finally {
      setCarregando(false);
    }
  }

  return (
    <button
      type="button"
      onClick={buscar}
      disabled={!podeBuscar}
      title={digits.length === 14 ? 'Importar dados publicos da Receita Federal' : 'Informe um CNPJ valido (14 digitos)'}
      // btn-primario = azul do tema + fonte branca. Nao chumbar a cor: assim ele acompanha o tema
      // ativo (Azul Royal, Azul Escuro...) em vez de virar uma mancha fixa quando o tema muda.
      className="btn-primario"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap',
        opacity: podeBuscar ? 1 : 0.5,
        cursor: podeBuscar ? 'pointer' : 'not-allowed',
      }}
    >
      {carregando ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
      Buscar Informacoes
    </button>
  );
}
